import type { Entry } from 'contentful';
import type {
  INavigation,
  INavigationItem,
  ITextImageTeaser
} from '~~/lib/ContentfulService';
import type {
  MinifiedSecondaryNavigation,
  MinifiedSecondaryNavigationItem
} from '~/types/MinifiedSecondaryNavigation';
import { compactLinkReference } from './compactLinkReference';
import { compactInternalPageReference } from './compactInternalPageReference';
import { compactPayloadEntrySys } from './compactPayloadEntrySys';
import type { SerializableRecord } from '../model';
import { getContentTypeId } from './getContentTypeId';
import { isSerializableRecord } from './isSerializableRecord';
import { isDefined } from '~/utils/guards/isDefined';

function compactAllowedBaslerIdGroups(
  groups: unknown[] | undefined
): unknown[] | undefined {
  if (!groups?.length) {
    return undefined;
  }

  const compacted = groups
    .filter(isDefined)
    .map((group) => {
      if (!isSerializableRecord(group)) {
        return group;
      }

      const groupSys = isSerializableRecord(group.sys) ? group.sys : undefined;
      const sys = groupSys ? compactPayloadEntrySys(groupSys) : undefined;
      const urn = sys?.urn ?? groupSys?.urn;
      const linkType = sys?.linkType ?? groupSys?.linkType;

      if (!urn && !linkType) {
        return undefined;
      }

      return {
        sys: {
          ...(urn ? { urn } : {}),
          ...(linkType ? { linkType } : {})
        }
      };
    })
    .filter(isDefined);

  return compacted.length > 0 ? compacted : undefined;
}

function minifySecondaryNavigationItem(
  item: INavigationItem
): MinifiedSecondaryNavigationItem | undefined {
  const sys = compactPayloadEntrySys(item.sys);
  if (!sys) {
    return undefined;
  }

  const fields = item.fields;
  const navigationItems = fields.navigationItems
    ?.map((child: INavigationItem) => minifySecondaryNavigationItem(child))
    .filter(isDefined);

  const minifiedFields: MinifiedSecondaryNavigationItem['fields'] = {
    linkText: fields.linkText,
    showInNavigation: fields.showInNavigation ?? true
  };

  if (fields.linkTarget) {
    minifiedFields.linkTarget = fields.linkTarget;
  }

  if (fields.visibility && fields.visibility !== 'Public') {
    minifiedFields.visibility = fields.visibility;
  }

  const internalPageFields =
    fields.internalPage && isSerializableRecord(fields.internalPage)
      ? (fields.internalPage as SerializableRecord).fields
      : undefined;
  const allowedGroups =
    compactAllowedBaslerIdGroups(
      fields.allowedBaslerIdGroups as unknown[] | undefined
    ) ??
    (fields.visibility === 'Restricted' &&
    isSerializableRecord(internalPageFields)
      ? compactAllowedBaslerIdGroups(
          internalPageFields.allowedBaslerIdGroups as unknown[] | undefined
        )
      : undefined);
  if (allowedGroups) {
    minifiedFields.allowedBaslerIdGroups = allowedGroups;
  }

  if (fields.moreLinkText) {
    minifiedFields.moreLinkText = fields.moreLinkText;
  }

  if (fields.internalPage && isSerializableRecord(fields.internalPage)) {
    const internalPage = compactInternalPageReference(
      fields.internalPage as SerializableRecord
    );
    if (internalPage) {
      minifiedFields.internalPage =
        internalPage as unknown as MinifiedSecondaryNavigationItem['fields']['internalPage'];
    }
  }

  if (fields.externalLink && isSerializableRecord(fields.externalLink)) {
    const externalLink = compactLinkReference(
      fields.externalLink as SerializableRecord
    );
    if (externalLink) {
      minifiedFields.externalLink =
        externalLink as unknown as MinifiedSecondaryNavigationItem['fields']['externalLink'];
    }
  }

  if (navigationItems?.length) {
    minifiedFields.navigationItems =
      navigationItems as unknown as MinifiedSecondaryNavigationItem['fields']['navigationItems'];
  }

  return {
    sys,
    fields: minifiedFields
  };
}

function stripImageLayoutFields(image: SerializableRecord): SerializableRecord {
  if (!isSerializableRecord(image.fields)) {
    return image;
  }

  const fields = { ...image.fields };
  delete fields.imageWidth;
  delete fields.imageCentered;
  delete fields.imageCenteredInRow;
  delete fields.imageFormat;

  return {
    ...image,
    fields
  };
}

function minifySecondaryNavigationTeaserEntry(
  entry: Entry<unknown>
): Entry<unknown> | undefined {
  const record = entry as unknown as SerializableRecord;
  const contentTypeId = getContentTypeId(record);

  if (contentTypeId === 'link' || contentTypeId === 'download') {
    return compactLinkReference(record) as Entry<unknown> | undefined;
  }

  if (contentTypeId === 'textImageTeaser') {
    const teaser = entry as ITextImageTeaser;
    const sys = compactPayloadEntrySys(teaser.sys);
    if (!sys) {
      return undefined;
    }

    const link =
      teaser.fields.link && isSerializableRecord(teaser.fields.link)
        ? compactLinkReference(teaser.fields.link as SerializableRecord)
        : undefined;
    const image =
      teaser.fields.image && isSerializableRecord(teaser.fields.image)
        ? stripImageLayoutFields(teaser.fields.image as SerializableRecord)
        : undefined;

    return {
      sys,
      fields: {
        headline: teaser.fields.headline,
        ...(teaser.fields.text ? { text: teaser.fields.text } : {}),
        ...(image ? { image } : {}),
        ...(link ? { link } : {})
      }
    } as ITextImageTeaser;
  }

  return entry;
}

function minifyTitleLink(
  link: INavigation['fields']['linkForSecondaryNavigationTitle']
): INavigation['fields']['linkForSecondaryNavigationTitle'] | undefined {
  if (!link || !isSerializableRecord(link)) {
    return undefined;
  }

  return compactLinkReference(
    link as SerializableRecord
  ) as unknown as INavigation['fields']['linkForSecondaryNavigationTitle'];
}

/**
 * Minifies secondary navigation for the Nuxt payload: keeps only fields used
 * by PageHeader, checkout breadcrumb, and CustomerAccountLayout navigation.
 *
 * Expects the navigation tree to already have passed through
 * {@link ContentfulTransformerService.sanitizeContentfulPayload}.
 */
export function minifySecondaryNavigation(
  navigation: INavigation | null | undefined
): MinifiedSecondaryNavigation | undefined {
  if (!navigation?.fields) {
    return undefined;
  }

  const { fields } = navigation;
  const navigationItems = fields.navigationItems
    ?.map((item: INavigationItem) => minifySecondaryNavigationItem(item))
    .filter(isDefined);

  const linksForSecondaryNavigation = fields.linksForSecondaryNavigation
    ?.map((entry: Entry<unknown>) =>
      minifySecondaryNavigationTeaserEntry(entry)
    )
    .filter(isDefined);

  const minified: MinifiedSecondaryNavigation = {
    fields: {
      navigationItems: navigationItems ?? []
    }
  };

  if (fields.titleForSecondaryNavigation) {
    minified.fields.titleForSecondaryNavigation =
      fields.titleForSecondaryNavigation;
  }

  if (fields.iconForSecondaryNavigation) {
    minified.fields.iconForSecondaryNavigation =
      fields.iconForSecondaryNavigation;
  }

  const titleLink = minifyTitleLink(fields.linkForSecondaryNavigationTitle);
  if (titleLink) {
    minified.fields.linkForSecondaryNavigationTitle = titleLink;
  }

  if (linksForSecondaryNavigation?.length) {
    minified.fields.linksForSecondaryNavigation = linksForSecondaryNavigation;
  }

  return minified;
}

/**
 * Extracts minified secondary navigation from a content page and removes the
 * heavy reference from `page.fields` to avoid duplicating it in the payload.
 */
export function extractMinifiedSecondaryNavigationFromPage(
  page: { fields?: { secondaryNavigation?: INavigation } } | null | undefined
): MinifiedSecondaryNavigation | undefined {
  const secondaryNavigation = page?.fields?.secondaryNavigation;
  if (!secondaryNavigation) {
    return undefined;
  }

  const minified = minifySecondaryNavigation(secondaryNavigation);
  if (page?.fields) {
    delete page.fields.secondaryNavigation;
  }

  return minified;
}
